import { useEffect, useState } from 'react';
import type {
  ClientWithTasks,
  Task,
  TaskAttachment,
  TaskLink,
  TaskStatus,
} from '../../types/models';
import { computeDue } from '../../lib/due';
import { Select } from '../ui/Select';
import { DatePicker } from '../ui/DatePicker';
import { RichText } from '../ui/RichText';
import {
  BoltIcon,
  CloseIcon,
  CursorSpark,
  LinkIcon,
  PaperclipIcon,
  PlusIcon,
  TrashIcon,
} from '../Icon';
import btn from '../../styles/buttons.module.css';
import form from '../../styles/forms.module.css';
import styles from './TaskDrawer.module.css';

interface TaskDrawerProps {
  task: Task;
  client: ClientWithTasks;
  /** Full client roster, used to let the user move this task to another client. */
  allClients: ClientWithTasks[];
  onClose: () => void;
  onUpdate: (patch: Partial<Task>) => void;
  onToggle: () => void;
  onDelete: () => void;
  onCursor: (title: string) => void;
  onToggleLineup: () => void;
  onUploadAttachment: (file: File) => Promise<TaskAttachment>;
}

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To do' },
  { value: 'doing', label: 'Doing' },
  { value: 'stalled', label: 'Stalled' },
  { value: 'done', label: 'Done' },
];

const PRIORITY_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '1', label: 'Urgent' },
  { value: '2', label: 'High' },
  { value: '3', label: 'Medium' },
  { value: '4', label: 'Low' },
];

function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function TaskDrawer({
  task,
  client,
  allClients,
  onClose,
  onUpdate,
  onToggle,
  onDelete,
  onCursor,
  onToggleLineup,
  onUploadAttachment,
}: TaskDrawerProps) {
  // Title/estimate save on blur; parent remounts on task change (via `key`).
  const [title, setTitle] = useState(task.title);
  const [est, setEst] = useState(task.est);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const commit = (patch: Partial<Task>) => onUpdate(patch);

  const links = task.linkItems ?? [];
  const attachments = task.attachments ?? [];

  const addLink = () => {
    const url = normalizeUrl(newLinkUrl);
    if (!url) return;
    const link: TaskLink = {
      id: crypto.randomUUID(),
      url,
      label: newLinkLabel.trim() || undefined,
    };
    commit({ linkItems: [...links, link] });
    setNewLinkUrl('');
    setNewLinkLabel('');
  };

  const removeLink = (id: string) => {
    commit({ linkItems: links.filter((l) => l.id !== id) });
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const att = await onUploadAttachment(file);
      commit({ attachments: [...attachments, att] });
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (id: string) => {
    commit({ attachments: attachments.filter((a) => a.id !== id) });
  };

  // Re-parenting a task drops its old project — projects are per-client, so
  // the previous projectId can't carry over to the new owner.
  const moveToClient = (clientId: string) => {
    if (clientId === task.clientId) return;
    commit({ clientId, projectId: '' });
  };

  const created = task.createdAt
    ? new Date(task.createdAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <>
      <button
        type="button"
        className={styles.overlay}
        onClick={onClose}
        aria-label="Close task"
      />
      <aside className={`${styles.drawer} sb-scroll`} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div className={styles.client}>{client.name}</div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon size={16} />
          </button>
        </div>

        <div className={styles.body}>
          <input
            className={form.titleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title !== task.title && commit({ title: title.trim() })}
            placeholder="Task title"
            aria-label="Task title"
          />

          <div className={styles.statusRow}>
            <button
              type="button"
              className={`${styles.doneToggle} ${task.done ? styles.doneToggleOn : ''}`}
              onClick={onToggle}
            >
              {task.done ? '☑ Done' : '☐ Mark done'}
            </button>
            <div className={styles.statusSelect}>
              <Select
                value={task.done ? 'done' : task.status}
                options={STATUS_OPTIONS}
                ariaLabel="Status"
                onChange={(v) => {
                  const status = v as TaskStatus;
                  commit({ status, done: status === 'done' });
                }}
              />
            </div>
            <button
              type="button"
              className={`${styles.lineupToggle} ${task.inLineup ? styles.lineupToggleOn : ''}`}
              onClick={onToggleLineup}
              aria-pressed={task.inLineup ?? false}
              title={task.inLineup ? 'Remove from Lineup' : 'Add to Lineup'}
            >
              <BoltIcon size={13} />
              {task.inLineup ? 'In Lineup' : 'Add to Lineup'}
            </button>
          </div>

          <div className={form.grid2}>
            <div className={form.field}>
              <label className={form.label}>Client</label>
              <Select
                value={client.id}
                options={allClients.map((c) => ({ value: c.id, label: c.name }))}
                ariaLabel="Client"
                onChange={moveToClient}
              />
            </div>
            <div className={form.field}>
              <label className={form.label}>Due date</label>
              <DatePicker
                value={task.dueDate ?? ''}
                ariaLabel="Due date"
                onChange={(v) => commit({ dueDate: v, ...computeDue(v) })}
              />
            </div>
            <div className={form.field}>
              <label className={form.label}>Estimate</label>
              <input
                className={form.input}
                value={est}
                onChange={(e) => setEst(e.target.value)}
                onBlur={() => est !== task.est && commit({ est: est.trim() })}
                placeholder="e.g. 2h"
                aria-label="Estimate"
              />
            </div>
            <div className={form.field}>
              <label className={form.label}>Project</label>
              <Select
                value={task.projectId}
                options={[
                  { value: '', label: 'Loose task' },
                  ...client.projects.map((p) => ({ value: p.id, label: p.name })),
                ]}
                ariaLabel="Project"
                onChange={(v) => commit({ projectId: v })}
              />
            </div>
            <div className={form.field}>
              <label className={form.label}>Priority</label>
              <Select
                value={String(task.priority ?? 0)}
                options={PRIORITY_OPTIONS}
                ariaLabel="Priority"
                onChange={(v) => commit({ priority: Number(v) })}
              />
            </div>
          </div>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Description</h3>
            <RichText
              value={task.notes}
              placeholder="Add context, decisions, acceptance criteria…"
              onSave={(md) => commit({ notes: md })}
            />
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Links {links.length > 0 && <span className={styles.count}>{links.length}</span>}
            </h3>
            {links.length > 0 && (
              <ul className={styles.refList}>
                {links.map((l) => (
                  <li key={l.id} className={styles.refRow}>
                    <LinkIcon size={13} className={styles.refIcon} />
                    <a
                      className={styles.refLink}
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {l.label ?? hostname(l.url)}
                    </a>
                    <button
                      type="button"
                      className={styles.refRemove}
                      onClick={() => removeLink(l.id)}
                      aria-label="Remove link"
                    >
                      <TrashIcon size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className={styles.linkForm}>
              <input
                className={form.input}
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addLink()}
                placeholder="Paste a URL…"
                aria-label="Link URL"
              />
              <input
                className={`${form.input} ${styles.linkLabel}`}
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addLink()}
                placeholder="Label (optional)"
                aria-label="Link label"
              />
              <button
                type="button"
                className={styles.addBtn}
                onClick={addLink}
                aria-label="Add link"
              >
                <PlusIcon size={14} />
              </button>
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Attachments{' '}
              {attachments.length > 0 && (
                <span className={styles.count}>{attachments.length}</span>
              )}
            </h3>
            {attachments.length > 0 && (
              <ul className={styles.refList}>
                {attachments.map((a) => (
                  <li key={a.id} className={styles.refRow}>
                    <PaperclipIcon size={13} className={styles.refIcon} />
                    <a
                      className={styles.refLink}
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {a.name}
                    </a>
                    {a.size ? <span className={styles.refMeta}>{formatBytes(a.size)}</span> : null}
                    <button
                      type="button"
                      className={styles.refRemove}
                      onClick={() => removeAttachment(a.id)}
                      aria-label="Remove attachment"
                    >
                      <TrashIcon size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label className={styles.uploadDrop}>
              <input
                type="file"
                className={styles.hiddenInput}
                onChange={(e) => void handleFile(e.target.files?.[0])}
              />
              <PaperclipIcon size={14} />
              {uploading ? 'Uploading…' : 'Upload a file'}
            </label>
          </section>

          {task.labels && task.labels.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Labels</h3>
              <div className={styles.labels}>
                {task.labels.map((l) => (
                  <span key={l} className={styles.labelChip}>
                    {l}
                  </span>
                ))}
              </div>
            </section>
          )}

          {created && <div className={styles.metaLine}>Created {created}</div>}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={`${btn.btn} ${btn.cta} ${styles.handBtn}`}
            onClick={() => onCursor(task.title)}
          >
            <CursorSpark size={14} />
            Hand to Cursor
          </button>
          <button type="button" className={styles.deleteBtn} onClick={onDelete}>
            Delete
          </button>
        </div>
      </aside>
    </>
  );
}
